import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import { useAppContext } from '../../context/AppContext';
import { useTheme } from '../../context/ThemeProvider';
import { useScrollSync } from '../../hooks/useScrollSync';
import { MermaidDiagram } from '../Mermaid/MermaidDiagram';

const PreviewPanel = () => {
  const { state } = useAppContext();
  const { isDark } = useTheme();
  const [isScrollSynced, setIsScrollSynced] = useState(true);

  // Initialize scroll synchronization
  useScrollSync({
    enabled: isScrollSynced,
    editorSelector: '.cm-scroller',
    previewSelector: '[data-preview-panel] .overflow-auto'
  });

  // Process content to remove HTML comments
  const processedContent = useMemo(() => {
    // Remove HTML comments while preserving the content structure
    return state.document.content.replace(/<!--[\s\S]*?-->/g, '');
  }, [state.document.content]);

  const proseClassName = useMemo(() => `
    cs-markdown-content prose prose-lg max-w-none
    ${isDark ? 'prose-invert' : 'prose-gray'}
    prose-headings:font-semibold prose-headings:tracking-tight
    prose-h1:text-3xl prose-h1:border-b prose-h1:pb-3 prose-h1:mb-6 prose-h1:mt-8 first:prose-h1:mt-0
    prose-h2:text-2xl prose-h2:border-b prose-h2:pb-2 prose-h2:mb-4 prose-h2:mt-8
    prose-h3:text-xl prose-h3:mb-3 prose-h3:mt-6
    prose-h4:text-lg prose-h4:mb-3 prose-h4:mt-6
    prose-h5:text-base prose-h5:mb-2 prose-h5:mt-4
    prose-h6:text-sm prose-h6:mb-2 prose-h6:mt-4
    prose-p:leading-relaxed prose-p:mb-4
    prose-pre:rounded-lg prose-pre:border prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:shadow-sm
    prose-code:rounded-md prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm
    prose-code:before:content-none prose-code:after:content-none
    prose-blockquote:border-l-4 prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:my-6
    prose-blockquote:rounded-r-lg prose-blockquote:shadow-sm
    prose-table:border-collapse prose-table:rounded-lg prose-table:overflow-hidden prose-table:shadow-md
    prose-th:border prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-semibold prose-th:bg-gray-50 dark:prose-th:bg-gray-700
    prose-td:border prose-td:px-4 prose-td:py-3
    prose-ul:space-y-2 prose-ol:space-y-2
    prose-li:leading-relaxed
    prose-a:font-medium prose-a:no-underline prose-a:decoration-2 prose-a:underline-offset-2
    hover:prose-a:underline prose-a:transition-all prose-a:duration-200
    prose-strong:font-semibold prose-em:italic
    prose-img:rounded-lg prose-img:shadow-sm
    prose-hr:border-t-2 prose-hr:my-8
    ${!isDark ? 'text-gray-900' : ''}
  `.replace(/\s+/g, ' ').trim(), [isDark]);

  // Calculate reading time estimate (use processed content)
  const readingTime = useMemo(() => {
    const words = processedContent.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200); // Average reading speed
    return minutes;
  }, [processedContent]);

  // Show enhanced placeholder when no content
  if (!state.document.content.trim()) {
    return (
      <div className="h-full flex flex-col cs-panel">
        {/* Preview Header */}
        <div className="cs-panel-header flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="font-medium text-gray-700 dark:text-gray-300">Preview</span>
          </div>
          
          <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Preview</span>
          </div>
        </div>

        {/* Empty State */}
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Live Preview</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Your markdown will render here with professional styling as you type.
            </p>
            
            <div className="text-xs text-gray-500 dark:text-gray-500 space-y-1">
              <div className="flex items-center justify-center space-x-1">
                <span>✓ Syntax highlighting</span>
              </div>
              <div className="flex items-center justify-center space-x-1">
                <span>✓ Tables & task lists</span>
              </div>
              <div className="flex items-center justify-center space-x-1">
                <span>✓ Math expressions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col cs-panel" data-preview-panel>
      {/* Enhanced Preview Header */}
      <div className="cs-panel-header flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="font-medium text-gray-700 dark:text-gray-300">Preview</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
            <span>{readingTime} min read</span>
            <span>•</span>
            <span>Live Preview</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsScrollSynced(!isScrollSynced)}
            className={`cs-btn-ghost px-2 py-1 text-xs ${
              isScrollSynced ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
            }`}
            title="Synchronize scrolling between editor and preview"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline ml-1">Scroll Sync</span>
          </button>
        </div>
      </div>

      {/* Enhanced Preview Content */}
      <div className="flex-1 overflow-auto relative" style={{ scrollBehavior: 'smooth' }}>
        <div className="p-6 md:p-8 cs-animate-fade-in">
          <div className={proseClassName}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={{
                // Enhanced code blocks with Mermaid diagram support
                code: ({ className, children, ...props }) => {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  
                  // Check if this is an inline code element
                  const isInline = !className || !className.startsWith('language-');
                  
                  // Handle Mermaid diagrams
                  if (language === 'mermaid' && !isInline) {
                    return (
                      <div className="my-6">
                        <MermaidDiagram 
                          chart={String(children).trim()} 
                          className="w-full"
                        />
                      </div>
                    );
                  }
                  
                  return !isInline ? (
                    <div className="relative group">
                      <pre className={`language-${language} bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto border border-gray-200 dark:border-gray-700 text-sm`}>
                        <code className={`${className} text-gray-800 dark:text-gray-200`} {...props}>
                          {children}
                        </code>
                      </pre>
                    </div>
                  ) : (
                    <code 
                      className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded text-sm font-mono border border-gray-200 dark:border-gray-700" 
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                // Professional image rendering with badge detection for inline display
                img: ({ alt, src, title, ...props }) => {
                  // Detect badges by URL patterns (shields.io, img.shields.io, etc.)
                  const isBadge = src && (
                    src.includes('shields.io') || 
                    src.includes('badge') || 
                    src.includes('img.shields.io') ||
                    src.includes('/badge/') ||
                    (alt && alt.toLowerCase().includes('badge'))
                  );
                  
                  if (isBadge) {
                    // Render badges inline without wrapping div
                    return (
                      <img 
                        alt={alt || 'Badge'} 
                        src={src} 
                        title={title}
                        className="inline-block align-middle mr-2 mb-1"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                        {...props}
                      />
                    );
                  }
                  
                  // Regular images with proper aspect ratios
                  return (
                    <div className="my-4 flex justify-center">
                      <img 
                        alt={alt || 'Image'} 
                        src={src} 
                        title={title}
                        className="max-w-full h-auto rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                        {...props}
                      />
                    </div>
                  );
                },
                // Enhanced tables with GitHub styling
                table: ({ children, ...props }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse border border-gray-200 dark:border-gray-700 rounded-lg" {...props}>
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children, ...props }) => (
                  <thead className="bg-gray-50 dark:bg-gray-800" {...props}>
                    {children}
                  </thead>
                ),
                th: ({ children, ...props }) => (
                  <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700" {...props}>
                    {children}
                  </th>
                ),
                td: ({ children, ...props }) => (
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700" {...props}>
                    {children}
                  </td>
                ),
                // Enhanced blockquotes with GitHub styling
                blockquote: ({ children, ...props }) => (
                  <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 dark:bg-blue-900/20 rounded-r-lg" {...props}>
                    <div className="text-gray-700 dark:text-gray-300 italic">
                      {children}
                    </div>
                  </blockquote>
                ),
                // Professional heading hierarchy with strong contrast for light theme
                h1: ({ children, ...props }) => (
                  <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-4 mt-8 pb-2 border-b border-gray-300 dark:border-gray-700" {...props}>
                    {children}
                  </h1>
                ),
                h2: ({ children, ...props }) => (
                  <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-3 mt-6 pb-1 border-b border-gray-300 dark:border-gray-700" {...props}>
                    {children}
                  </h2>
                ),
                h3: ({ children, ...props }) => (
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2 mt-4" {...props}>
                    {children}
                  </h3>
                ),
                h4: ({ children, ...props }) => (
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2 mt-3" {...props}>
                    {children}
                  </h4>
                ),
                h5: ({ children, ...props }) => (
                  <h5 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-1 mt-3" {...props}>
                    {children}
                  </h5>
                ),
                h6: ({ children, ...props }) => (
                  <h6 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1 mt-2" {...props}>
                    {children}
                  </h6>
                ),
                // Enhanced lists with proper spacing
                ul: ({ children, ...props }) => (
                  <ul className="list-disc pl-6 my-3 space-y-1 text-gray-700 dark:text-gray-300" {...props}>
                    {children}
                  </ul>
                ),
                ol: ({ children, ...props }) => (
                  <ol className="list-decimal pl-6 my-3 space-y-1 text-gray-700 dark:text-gray-300" {...props}>
                    {children}
                  </ol>
                ),
                li: ({ children, ...props }) => {
                  // Check if this is a task list item
                  const childrenArray = Array.isArray(children) ? children : [children];
                  const hasCheckbox = childrenArray.some(child => 
                    typeof child === 'object' && child !== null && 
                    'type' in child && child.type === 'input' && 
                    'props' in child && child.props.type === 'checkbox'
                  );

                  if (hasCheckbox) {
                    return (
                      <li className="flex items-start space-x-3 text-gray-700 dark:text-gray-300 leading-relaxed list-none" {...props}>
                        <div className="flex items-center justify-center w-4 h-4 mt-1">
                          {childrenArray.map((child, index) => {
                            if (typeof child === 'object' && child !== null && 
                                'type' in child && child.type === 'input' && 
                                'props' in child && child.props.type === 'checkbox') {
                              return (
                                <div key={index} className="relative">
                                  <input
                                    type="checkbox"
                                    checked={child.props.checked || false}
                                    disabled={child.props.disabled}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    readOnly
                                  />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                        <div className="flex-1">
                          {childrenArray.filter(child => 
                            !(typeof child === 'object' && child !== null && 
                              'type' in child && child.type === 'input')
                          )}
                        </div>
                      </li>
                    );
                  }

                  return (
                    <li className="text-gray-700 dark:text-gray-300 leading-relaxed" {...props}>
                      {children}
                    </li>
                  );
                },
                // Handle HTML elements like details/summary
                details: ({ children, ...props }) => (
                  <details className="my-4 border border-gray-200 dark:border-gray-700 rounded-lg" {...props}>
                    {children}
                  </details>
                ),
                summary: ({ children, ...props }) => (
                  <summary className="cursor-pointer p-3 bg-gray-50 dark:bg-gray-800 rounded-t-lg font-medium text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" {...props}>
                    {children}
                  </summary>
                )
              }}
            >
              {processedContent}
            </ReactMarkdown>
          </div>
        </div>
        
        {/* Subtle focus indicator */}
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-green-500/30 to-transparent"></div>
      </div>

      {/* Mobile Reading Time */}
      <div className="md:hidden px-4 py-2 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center justify-between">
          <span>{readingTime} min read</span>
          <span>Preview</span>
        </div>
      </div>
    </div>
  );
};

export default PreviewPanel;